import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faGavel, faEuroSign, faTrophy, faClock, faUsers, faEye,
  faChartLine, faCalendarAlt, faInfoCircle, faTimes, faEdit
} from '@fortawesome/free-solid-svg-icons';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../services/api';
import SupplierNavbar from '../../components/supplier/SupplierNavbar';
import { initializeSocket, joinAuctionsList, leaveAuctionsList, onAuctionsListUpdate, offAuctionsListUpdate, joinAuction, leaveAuction, onAuctionUpdate, offAuctionUpdate } from '../../services/socketService';

interface Auction {
  _id: string;
  auctionNumber: string;
  title: string;
  description?: string;
  masterArticleNumber: string;
  masterArticleName: string;
  validFrom: string;
  validUntil: string;
  calendarWeek: number;
  year: number;
  biddingStartTime: string;
  biddingEndTime: string;
  minimumBidIncrement: number;
  unit: string;
  currentLowestBid?: number;
  currentLowestBidder?: string;
  totalBidsCount: number;
  status: 'draft' | 'scheduled' | 'active' | 'ended' | 'awarded' | 'cancelled';
  myBids?: any[];
  myCurrentBid?: any;
  hasPlacedBid?: boolean;
}

interface Stats {
  totalAuctions: number;
  activeAuctions: number;
  myBidsCount: number;
  wonAuctions: number;
  currentlyWinningCount: number;
}

const SupplierAuctions: React.FC = () => {
  const navigate = useNavigate();
  const { masterToken } = useParams<{ masterToken: string }>();
  const [auctions, setAuctions] = useState<Auction[]>([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<Stats | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [supplierInfo, setSupplierInfo] = useState<any>(null);
  
  // Modal States
  const [showBidModal, setShowBidModal] = useState(false);
  const [selectedAuction, setSelectedAuction] = useState<Auction | null>(null);
  const [bidAmount, setBidAmount] = useState('');
  const [submittingBid, setSubmittingBid] = useState(false);
  
  // Toast State
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' | 'info' } | null>(null);
  
  // Countdown State
  const [currentTime, setCurrentTime] = useState(new Date());

  // Update time every second for countdowns
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    // Load supplier info from session storage
    const storedSupplierInfo = sessionStorage.getItem('supplierInfo');
    if (storedSupplierInfo) {
      setSupplierInfo(JSON.parse(storedSupplierInfo));
    }
    
    loadAuctions();
    loadStats();

    // Initialize Socket.IO
    initializeSocket();
    joinAuctionsList();

    // Listen for real-time updates
    const handleAuctionsUpdate = () => {
      console.log('📢 Received auctions-list-updated event');
      loadAuctions();
      loadStats();
    };

    onAuctionsListUpdate(handleAuctionsUpdate);

    return () => {
      offAuctionsListUpdate(handleAuctionsUpdate);
      leaveAuctionsList();
    };
  }, [statusFilter]);

  // Subscribe to individual auction updates for real-time bid updates
  useEffect(() => {
    if (auctions.length === 0) return;

    console.log(`🔗 Joining ${auctions.length} auction rooms for real-time updates`);

    // Join all auction rooms
    auctions.forEach(auction => {
      joinAuction(auction._id);
      console.log(`  → Joined auction room: ${auction._id}`);
    });

    // Listen for individual auction updates
    const handleIndividualAuctionUpdate = async (data: any) => {
      console.log('📢 [TABLE] Individual auction updated:', data);
      
      // Reload the specific auction from server to get myCurrentBid
      try {
        const response = await api.get(`/supplier-portal/auctions/${data.auctionId}`);
        const updatedAuction = response.data.auction;
        
        console.log('🔍 [TABLE] Server response for auction reload:', {
          auctionId: data.auctionId,
          hasMyCurrentBid: !!response.data.myCurrentBid,
          myCurrentBid: response.data.myCurrentBid,
          currentLowestBid: updatedAuction.currentLowestBid
        });
        
        setAuctions(prevAuctions => 
          prevAuctions.map(auction => {
            if (auction._id === data.auctionId) {
              console.log(`  ✅ Replaced auction ${auction._id} with fresh data from server`);
              return {
                ...updatedAuction,
                myCurrentBid: response.data.myCurrentBid,
                hasPlacedBid: response.data.myBids?.length > 0,
                masterArticleNumber: updatedAuction.masterArticleId?.articleNumber || updatedAuction.masterArticleNumber,
                masterArticleName: updatedAuction.masterArticleId?.name || updatedAuction.masterArticleName
              };
            }
            return auction;
          })
        );
      } catch (error) {
        console.error('Error reloading auction after update:', error);
        // Fallback: Just update the numbers
        setAuctions(prevAuctions => 
          prevAuctions.map(auction => {
            if (auction._id === data.auctionId) {
              return {
                ...auction,
                currentLowestBid: data.currentLowestBid ?? auction.currentLowestBid,
                currentLowestBidder: data.currentLowestBidder ?? auction.currentLowestBidder,
                totalBidsCount: data.totalBidsCount ?? auction.totalBidsCount
              };
            }
            return auction;
          })
        );
      }

      // Update stats as well
      loadStats();
    };

    onAuctionUpdate(handleIndividualAuctionUpdate);

    return () => {
      console.log(`🔌 Leaving ${auctions.length} auction rooms`);
      offAuctionUpdate(handleIndividualAuctionUpdate);
      auctions.forEach(auction => {
        leaveAuction(auction._id);
      });
    };
  }, [auctions.map(a => a._id).join(',')]); // Re-subscribe when auction IDs change

  // Live-Updates für Modal: WebSocket statt Polling
  useEffect(() => {
    if (!showBidModal || !selectedAuction?._id) return;

    // Join auction room for real-time updates
    joinAuction(selectedAuction._id);

    const handleModalAuctionUpdate = async (data: any) => {
      console.log('📢 Modal received auction-updated event:', data);
      
      // Lade vollständige Auktionsdaten inkl. myCurrentBid
      try {
        const response = await api.get(`/supplier-portal/auctions/${selectedAuction._id}`);
        const auctionData = response.data.auction;
        
        const fullAuction = {
          ...auctionData,
          myCurrentBid: response.data.myCurrentBid,
          currentLowestBid: auctionData.currentLowestBid,
          masterArticleNumber: auctionData.masterArticleId?.articleNumber || auctionData.masterArticleNumber,
          masterArticleName: auctionData.masterArticleId?.name || auctionData.masterArticleName
        };
        
        setSelectedAuction(fullAuction);
      } catch (error) {
        console.error('Error refreshing modal auction data:', error);
      }
    };

    onAuctionUpdate(handleModalAuctionUpdate);

    return () => {
      offAuctionUpdate(handleModalAuctionUpdate);
      leaveAuction(selectedAuction._id);
    };
  }, [showBidModal, selectedAuction?._id]);

  const loadAuctions = async () => {
    try {
      setLoading(true);
      const params: any = {};
      if (statusFilter) params.status = statusFilter;

      const response = await api.get('/supplier-portal/auctions', { params });
      setAuctions(response.data.auctions);
    } catch (error: any) {
      console.error('Error loading auctions:', error);
      
      // Check if no auction permission
      if (error.response?.status === 403) {
        showToast('Sie haben keine Berechtigung für Auktionen. Bitte kontaktieren Sie Ihren Administrator.', 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const response = await api.get('/supplier-portal/auctions/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  const showToast = (message: string, type: 'success' | 'error' | 'info' = 'info') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  };

  const getStatusBadge = (auction: Auction) => {
    const now = new Date();
    const biddingStart = new Date(auction.biddingStartTime);
    const biddingEnd = new Date(auction.biddingEndTime);

    if (auction.status === 'awarded') {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
          <FontAwesomeIcon icon={faTrophy} className="mr-1" />
          Vergeben
        </span>
      );
    }

    if (auction.status === 'ended') {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
          <FontAwesomeIcon icon={faClock} className="mr-1" />
          Beendet
        </span>
      );
    }

    if (auction.status === 'active' && now >= biddingStart && now <= biddingEnd) {
      const remaining = getRemainingTime(auction.biddingEndTime);
      if (remaining) {
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 animate-pulse">
            <FontAwesomeIcon icon={faGavel} className="mr-1" />
            Aktiv ({remaining.days}T {String(remaining.hours).padStart(2, '0')}:{String(remaining.minutes).padStart(2, '0')}:{String(remaining.seconds).padStart(2, '0')})
          </span>
        );
      }
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 animate-pulse">
          <FontAwesomeIcon icon={faGavel} className="mr-1" />
          Aktiv
        </span>
      );
    }

    if (auction.status === 'scheduled' || now < biddingStart) {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
          <FontAwesomeIcon icon={faClock} className="mr-1" />
          Geplant
        </span>
      );
    }

    return null;
  };

  const isWinning = (auction: Auction) => {
    return auction.myCurrentBid && auction.myCurrentBid.isCurrentLowest;
  };

  const hasStarted = (auction: Auction) => {
    const now = new Date();
    const biddingStart = new Date(auction.biddingStartTime);
    return now >= biddingStart;
  };

  const getCountdown = (startTime: string) => {
    const now = currentTime.getTime();
    const start = new Date(startTime).getTime();
    const diff = start - now;

    if (diff <= 0) return null;

    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((diff % (1000 * 60)) / 1000);

    return { days, hours, minutes, seconds };
  };

  const getRemainingTime = (endTime: string) => {
    const now = currentTime.getTime();
    const end = new Date(endTime).getTime();
    const diff = end - now;

    if (diff <= 0) return null;

    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((diff % (1000 * 60)) / 1000);

    return { days, hours, minutes, seconds };
  };

  const handleOpenBidModal = async (auction: Auction, e: React.MouseEvent) => {
    e.stopPropagation();
    
    // Lade vollständige Auktionsdaten vom Server
    try {
      const response = await api.get(`/supplier-portal/auctions/${auction._id}`);
      const auctionData = response.data.auction;
      
      // Merge mit myCurrentBid aus der Response
      const fullAuction = {
        ...auctionData,
        myCurrentBid: response.data.myCurrentBid,
        currentLowestBid: auctionData.currentLowestBid,
        masterArticleNumber: auctionData.masterArticleId?.articleNumber || auctionData.masterArticleNumber,
        masterArticleName: auctionData.masterArticleId?.name || auctionData.masterArticleName
      };
      
      setSelectedAuction(fullAuction);
      const currentBidAmount = response.data.myCurrentBid?.bidAmount;
      setBidAmount(currentBidAmount ? currentBidAmount.toFixed(2).replace('.', ',') : '');
      setShowBidModal(true);
    } catch (error) {
      console.error('Error loading auction details:', error);
      // Fallback: verwende die vorhandenen Daten
      setSelectedAuction(auction);
      const currentBidAmount = auction.myCurrentBid?.bidAmount;
      setBidAmount(currentBidAmount ? currentBidAmount.toFixed(2).replace('.', ',') : '');
      setShowBidModal(true);
    }
  };

  const handleCloseBidModal = () => {
    setShowBidModal(false);
    setSelectedAuction(null);
    setBidAmount('');
  };

  const handleBidAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value;
    // Erlaube leeres Feld
    if (value === '') {
      setBidAmount('');
      return;
    }
    // Erlaube nur Zahlen, Komma und Punkt
    value = value.replace(/[^0-9,.]/g, '');
    // Ersetze Komma durch Punkt für interne Verarbeitung (wird bei Anzeige wieder zu Komma)
    setBidAmount(value);
  };

  const handleBidAmountBlur = () => {
    if (bidAmount === '') return;
    
    // Konvertiere Komma zu Punkt
    let value = bidAmount.replace(',', '.');
    const numValue = parseFloat(value);
    
    if (!isNaN(numValue)) {
      // Formatiere auf 2 Dezimalstellen und ersetze Punkt durch Komma
      setBidAmount(numValue.toFixed(2).replace('.', ','));
    }
  };

  const handleSubmitBid = async () => {
    if (!selectedAuction || !bidAmount) return;

    try {
      setSubmittingBid(true);
      // Konvertiere Komma zu Punkt für API
      const bidValue = parseFloat(bidAmount.replace(',', '.'));
      
      await api.post(`/supplier-portal/auctions/${selectedAuction._id}/bid`, {
        bidAmount: bidValue
      });
      
      showToast('Gebot erfolgreich abgegeben!', 'success');
      handleCloseBidModal();
      loadAuctions();
      loadStats();
    } catch (error: any) {
      showToast(error.response?.data?.message || 'Fehler beim Abgeben des Gebots', 'error');
    } finally {
      setSubmittingBid(false);
    }
  };

  return (
    <>
      <SupplierNavbar 
        supplierName={supplierInfo?.name || 'Lieferant'}
        masterToken={masterToken}
        pendingQuestionnaires={0}
        totalArticles={0}
        activeAuctions={stats?.activeAuctions || 0}
      />
      <div className="w-full px-6 py-6">

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-5 gap-3 mb-4">
            <div className="bg-white rounded-lg shadow p-3">
              <div className="text-xs text-gray-600">Verfügbare Auktionen</div>
              <div className="text-xl font-bold text-gray-800">{stats.totalAuctions}</div>
            </div>
            <div className="bg-green-50 rounded-lg shadow p-3">
              <div className="text-xs text-green-600">Aktive Auktionen</div>
              <div className="text-xl font-bold text-green-800">{stats.activeAuctions}</div>
            </div>
            <div className="bg-blue-50 rounded-lg shadow p-3">
              <div className="text-xs text-blue-600">Meine Gebote</div>
              <div className="text-xl font-bold text-blue-800">{stats.myBidsCount}</div>
            </div>
            <div className="bg-yellow-50 rounded-lg shadow p-3">
              <div className="text-xs text-yellow-600">Derzeit führend</div>
              <div className="text-xl font-bold text-yellow-800">{stats.currentlyWinningCount}</div>
            </div>
            <div className="bg-purple-50 rounded-lg shadow p-3">
              <div className="text-xs text-purple-600">Gewonnen</div>
              <div className="text-xl font-bold text-purple-800">{stats.wonAuctions}</div>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="bg-white rounded-lg shadow p-3 mb-4">
          <div className="flex gap-3">
            <div className="flex-1">
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-orange-500"
              >
                <option value="">Alle Auktionen</option>
                <option value="active">Aktiv</option>
                <option value="scheduled">Geplant (startet bald)</option>
                <option value="ended">Beendet</option>
                <option value="awarded">Vergeben</option>
              </select>
            </div>
            <button
              onClick={() => setStatusFilter('')}
              className="px-4 py-2 text-sm bg-gray-200 hover:bg-gray-300 rounded-lg"
            >
              Zurücksetzen
            </button>
          </div>
        </div>

      {/* Auctions List */}
      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Lade Auktionen...</p>
        </div>
      ) : auctions.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <FontAwesomeIcon icon={faGavel} className="text-6xl text-gray-300 mb-4" />
          <p className="text-gray-600 text-lg">Derzeit keine Auktionen verfügbar</p>
          <p className="text-gray-500 text-sm mt-2">
            Neue Auktionen werden hier angezeigt, sobald sie für Sie freigegeben werden
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Auktion & Artikel
                  </th>
                  <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Start ab
                  </th>
                  <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Bis
                  </th>
                  <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Bestes Gebot
                  </th>
                  <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Ihr Gebot
                  </th>
                  <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Aktionen
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {auctions.map((auction) => {
                  const winning = isWinning(auction);
                  const started = hasStarted(auction);
                  
                  return (
                    <tr 
                      key={auction._id}
                      className={`hover:bg-gray-50 transition-colors ${winning ? 'bg-yellow-50' : ''}`}
                    >
                      {/* Auction Info */}
                      <td className="px-3 py-3">
                        <div>
                          <div className="text-sm font-bold text-gray-900 flex items-center gap-2">
                            {auction.title}
                            {winning && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-400 text-yellow-900 shadow-sm">
                                <FontAwesomeIcon icon={faTrophy} className="mr-1" />
                                Sie führen!
                              </span>
                            )}
                          </div>
                          <div className="text-xs text-gray-500 mt-0.5">
                            {auction.auctionNumber} • KW {auction.calendarWeek}/{auction.year}
                          </div>
                          <div className="text-xs text-gray-600 mt-0.5">
                            {auction.masterArticleName}
                          </div>
                        </div>
                      </td>
                      
                      {/* Status */}
                      <td className="px-3 py-3 text-center">
                        {getStatusBadge(auction)}
                      </td>
                      
                      {/* Bidding Start Time */}
                      <td className="px-3 py-3 text-center">
                        <div className="text-xs text-gray-700 font-semibold whitespace-nowrap">
                          {new Date(auction.biddingStartTime).toLocaleDateString('de-DE', {
                            day: '2-digit',
                            month: '2-digit',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </div>
                      </td>
                      
                      {/* Bidding End Time */}
                      <td className="px-3 py-3 text-center">
                        <div className="text-xs text-gray-700 font-semibold whitespace-nowrap">
                          {new Date(auction.biddingEndTime).toLocaleDateString('de-DE', {
                            day: '2-digit',
                            month: '2-digit',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </div>
                      </td>
                      
                      {/* Current Lowest Bid - Circle */}
                      <td className="px-3 py-3">
                        <div className="flex justify-center items-center">
                          {(auction.currentLowestBid !== undefined && auction.currentLowestBid !== null) ? (
                            <div className="flex flex-col items-center">
                              <div className="w-24 h-24 rounded-full bg-gradient-to-br from-green-400 to-green-600 flex flex-col items-center justify-center shadow-lg border-4 border-white">
                                <div className="text-2xl font-black text-white">€{auction.currentLowestBid.toFixed(2)}</div>
                                <div className="text-xs text-white/90 font-medium">/{auction.unit}</div>
                              </div>
                            </div>
                          ) : (
                            <div className="w-24 h-24 rounded-full bg-gray-200 flex items-center justify-center">
                              <span className="text-sm text-gray-400 font-medium">Kein Gebot</span>
                            </div>
                          )}
                        </div>
                      </td>
                      
                      {/* My Bid - Circle */}
                      <td className="px-3 py-3">
                        <div className="flex justify-center items-center">
                          {(auction.myCurrentBid?.bidAmount !== undefined && auction.myCurrentBid?.bidAmount !== null) ? (
                            <div className="flex flex-col items-center relative">
                              {winning ? (
                                // Winning bid - Yellow with trophy
                                <>
                                  <div className="w-24 h-24 rounded-full bg-gradient-to-br from-yellow-400 to-yellow-600 border-yellow-200 flex flex-col items-center justify-center shadow-lg border-4 border-white">
                                    <div className="text-2xl font-black text-white">€{auction.myCurrentBid.bidAmount.toFixed(2)}</div>
                                    <div className="text-xs text-white/90 font-medium">/{auction.unit}</div>
                                  </div>
                                  <div className="absolute -top-2 -right-2 bg-yellow-500 text-white rounded-full w-8 h-8 flex items-center justify-center shadow-lg border-2 border-white">
                                    <span className="text-lg">🏆</span>
                                  </div>
                                </>
                              ) : (
                                // Outbid - Red with "Unterboten" badge inside circle at top
                                <div className="w-24 h-24 rounded-full bg-gradient-to-br from-red-400 to-red-600 border-red-200 flex flex-col items-center justify-center shadow-lg border-4 border-white relative pt-5">
                                  <div className="absolute top-5 text-[9px] font-bold text-white/90 uppercase tracking-wider">
                                    Unterboten
                                  </div>
                                  <div className="text-xl font-black text-white mt-1">€{auction.myCurrentBid.bidAmount.toFixed(2)}</div>
                                  <div className="text-xs text-white/90 font-medium">/{auction.unit}</div>
                                </div>
                              )}
                            </div>
                          ) : (
                            <div className="w-24 h-24 rounded-full bg-gray-100 border-2 border-dashed border-gray-300 flex flex-col items-center justify-center">
                              <span className="text-xs text-gray-400 font-medium text-center">Noch kein<br/>Gebot</span>
                            </div>
                          )}
                        </div>
                      </td>
                      
                      {/* Actions */}
                      <td className="px-3 py-3">
                        <div className="flex flex-col items-center justify-center gap-1.5">
                          {!started ? (
                            <div className="text-center">
                              {(() => {
                                const countdown = getCountdown(auction.biddingStartTime);
                                if (!countdown) return null;
                                
                                return (
                                  <>
                                    <div className="text-xs text-gray-500 font-medium mb-1">
                                      Startet in
                                    </div>
                                    <div className="flex gap-1 mb-1">
                                      {countdown.days > 0 && (
                                        <div className="bg-blue-100 text-blue-700 px-2 py-1 rounded text-xs font-bold">
                                          <div className="text-lg leading-none">{countdown.days}</div>
                                          <div className="text-[9px] uppercase mt-0.5">Tag{countdown.days !== 1 ? 'e' : ''}</div>
                                        </div>
                                      )}
                                      <div className="bg-blue-100 text-blue-700 px-2 py-1 rounded text-xs font-bold">
                                        <div className="text-lg leading-none">{String(countdown.hours).padStart(2, '0')}</div>
                                        <div className="text-[9px] uppercase mt-0.5">Std</div>
                                      </div>
                                      <div className="bg-blue-100 text-blue-700 px-2 py-1 rounded text-xs font-bold">
                                        <div className="text-lg leading-none">{String(countdown.minutes).padStart(2, '0')}</div>
                                        <div className="text-[9px] uppercase mt-0.5">Min</div>
                                      </div>
                                      <div className="bg-blue-100 text-blue-700 px-2 py-1 rounded text-xs font-bold">
                                        <div className="text-lg leading-none">{String(countdown.seconds).padStart(2, '0')}</div>
                                        <div className="text-[9px] uppercase mt-0.5">Sek</div>
                                      </div>
                                    </div>
                                    <div className="text-[10px] text-gray-400 font-medium">
                                      {new Date(auction.biddingStartTime).toLocaleDateString('de-DE', {
                                        day: '2-digit',
                                        month: '2-digit',
                                        hour: '2-digit',
                                        minute: '2-digit'
                                      })}
                                    </div>
                                  </>
                                );
                              })()}
                            </div>
                          ) : (
                            <>
                              <button
                                onClick={(e) => handleOpenBidModal(auction, e)}
                                className="w-full inline-flex items-center justify-center px-3 py-1.5 border border-transparent text-xs font-medium rounded text-white bg-orange-500 hover:bg-orange-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 transition-colors"
                              >
                                <FontAwesomeIcon icon={faGavel} className="mr-1" />
                                {auction.hasPlacedBid ? 'Anpassen' : 'Bieten'}
                              </button>
                              <button
                                onClick={() => navigate(`/supplier/portal/${masterToken}/auctions/${auction._id}`)}
                                className="w-full inline-flex items-center justify-center px-3 py-1.5 border border-gray-300 text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 transition-colors"
                              >
                                <FontAwesomeIcon icon={faEye} className="mr-1" />
                                Details
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Bid Modal */}
      {showBidModal && selectedAuction && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="bg-orange-500 text-white px-6 py-4 flex justify-between items-center">
              <h2 className="text-xl font-bold flex items-center gap-2">
                <FontAwesomeIcon icon={faGavel} />
                Gebot abgeben
              </h2>
              <button
                onClick={handleCloseBidModal}
                className="text-white hover:text-gray-200 transition-colors"
              >
                <FontAwesomeIcon icon={faTimes} className="text-2xl" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6">
              {/* Live Countdown Timer */}
              {(() => {
                const remaining = getRemainingTime(selectedAuction.biddingEndTime);
                if (remaining) {
                  return (
                    <div className="mb-4 bg-gradient-to-r from-green-50 to-green-100 border-l-4 border-green-500 p-3 rounded-r-lg">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <FontAwesomeIcon icon={faClock} className="text-green-600 text-lg animate-pulse" />
                          <span className="text-sm font-semibold text-green-800">Verbleibende Zeit:</span>
                        </div>
                        <div className="flex gap-2">
                          {remaining.days > 0 && (
                            <div className="bg-green-600 text-white px-3 py-1.5 rounded-lg text-center min-w-[50px]">
                              <div className="text-xl font-black leading-none">{remaining.days}</div>
                              <div className="text-[9px] uppercase mt-0.5 opacity-90">Tag{remaining.days !== 1 ? 'e' : ''}</div>
                            </div>
                          )}
                          <div className="bg-green-600 text-white px-3 py-1.5 rounded-lg text-center min-w-[50px]">
                            <div className="text-xl font-black leading-none">{String(remaining.hours).padStart(2, '0')}</div>
                            <div className="text-[9px] uppercase mt-0.5 opacity-90">Std</div>
                          </div>
                          <div className="bg-green-600 text-white px-3 py-1.5 rounded-lg text-center min-w-[50px]">
                            <div className="text-xl font-black leading-none">{String(remaining.minutes).padStart(2, '0')}</div>
                            <div className="text-[9px] uppercase mt-0.5 opacity-90">Min</div>
                          </div>
                          <div className="bg-green-600 text-white px-3 py-1.5 rounded-lg text-center min-w-[50px]">
                            <div className="text-xl font-black leading-none">{String(remaining.seconds).padStart(2, '0')}</div>
                            <div className="text-[9px] uppercase mt-0.5 opacity-90">Sek</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                }
                return null;
              })()}
              
              {/* Auction Info - Kompakt */}
              <div className="mb-4 pb-4 border-b border-gray-200">
                <h3 className="font-bold text-xl mb-3 text-gray-900">{selectedAuction.title}</h3>
                <div className="grid grid-cols-4 gap-x-4 gap-y-1 text-xs">
                  <div className="col-span-2">
                    <span className="text-gray-500">Artikel:</span>
                    <div className="font-semibold text-gray-900">{selectedAuction.masterArticleName}</div>
                  </div>
                  <div>
                    <span className="text-gray-500">Auktionsnummer:</span>
                    <div className="font-semibold text-gray-900">{selectedAuction.auctionNumber}</div>
                  </div>
                  <div>
                    <span className="text-gray-500">KW:</span>
                    <div className="font-semibold text-gray-900">{selectedAuction.calendarWeek}/{selectedAuction.year}</div>
                  </div>
                  <div>
                    <span className="text-gray-500">Einheit:</span>
                    <div className="font-semibold text-gray-900">{selectedAuction.unit}</div>
                  </div>
                </div>
              </div>

              {/* Current Lowest Bid - Großer zentraler Live-Kreis */}
              {selectedAuction.currentLowestBid !== undefined && selectedAuction.currentLowestBid !== null && (
                <div className="mb-6 flex justify-center">
                  <div className="relative">
                    {/* Äußerer pulsierender Ring */}
                    <div className="absolute inset-0 bg-green-400 rounded-full animate-ping opacity-30"></div>
                    {/* Zweiter pulsierender Ring für mehr Tiefe */}
                    <div className="absolute inset-4 bg-green-300 rounded-full animate-ping opacity-20" style={{animationDelay: '0.3s'}}></div>
                    {/* Hauptkreis */}
                    <div className="relative w-56 h-56 rounded-full bg-gradient-to-br from-green-400 via-green-500 to-green-600 shadow-2xl flex flex-col items-center justify-center text-white transform hover:scale-110 transition-all duration-500 cursor-pointer">
                      <div className="text-sm uppercase tracking-widest font-bold opacity-90 mb-2">Aktuell Niedrigstes</div>
                      <div className="text-5xl font-black mb-1 animate-pulse">
                        € {selectedAuction.currentLowestBid.toFixed(2).replace('.', ',')}
                      </div>
                      <div className="text-base mt-1 opacity-90 font-semibold">pro {selectedAuction.unit}</div>
                      <div className="absolute bottom-4 text-xs opacity-75 uppercase tracking-wider">
                        <FontAwesomeIcon icon={faClock} className="mr-1 animate-spin" style={{animationDuration: '3s'}} />
                        Live
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Your Current Bid - Kompakt als Info-Box */}
              {selectedAuction.myCurrentBid?.bidAmount !== undefined && selectedAuction.myCurrentBid?.bidAmount !== null && (
                <div className="mb-4 p-3 bg-blue-50 border-l-4 border-blue-500 rounded-r-lg">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-blue-900">Ihr aktuelles Gebot:</span>
                    <span className="text-2xl font-bold text-blue-700">
                      € {selectedAuction.myCurrentBid.bidAmount.toFixed(2)}
                      <span className="text-sm font-normal text-blue-600 ml-1">/ {selectedAuction.unit}</span>
                    </span>
                  </div>
                </div>
              )}

              {/* Bid Input - Kompakter */}
              <div className="mb-4">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Ihr Gebot (pro {selectedAuction.unit})
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <span className="text-gray-500 text-base font-semibold">€</span>
                  </div>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={bidAmount}
                    onChange={handleBidAmountChange}
                    onBlur={handleBidAmountBlur}
                    className="block w-full pl-10 pr-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-xl font-bold text-gray-900"
                    placeholder="0,00"
                  />
                </div>
                <p className="mt-1.5 text-xs text-gray-500">
                  Mindestschrittweite: € {selectedAuction.minimumBidIncrement?.toFixed(2).replace('.', ',') || '0,01'}
                </p>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3">
                <button
                  onClick={handleCloseBidModal}
                  className="flex-1 px-4 py-3 border border-gray-300 text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500"
                >
                  Abbrechen
                </button>
                <button
                  onClick={handleSubmitBid}
                  disabled={submittingBid || !bidAmount}
                  className="flex-1 px-4 py-3 border border-transparent text-sm font-medium rounded-lg text-white bg-orange-500 hover:bg-orange-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {submittingBid ? (
                    <>
                      <FontAwesomeIcon icon={faClock} className="mr-2 animate-spin" />
                      Wird abgegeben...
                    </>
                  ) : (
                    <>
                      <FontAwesomeIcon icon={faGavel} className="mr-2" />
                      Gebot abgeben
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Toast Notification */}
      {toast && (
        <div className="fixed bottom-4 right-4 z-50 animate-slide-up">
          <div className={`rounded-lg shadow-2xl px-6 py-4 flex items-center gap-3 min-w-[300px] ${
            toast.type === 'success' ? 'bg-green-500 text-white' :
            toast.type === 'error' ? 'bg-red-500 text-white' :
            'bg-blue-500 text-white'
          }`}>
            <div className="text-2xl">
              {toast.type === 'success' ? '✓' : toast.type === 'error' ? '✕' : 'ℹ'}
            </div>
            <div className="flex-1 font-medium">{toast.message}</div>
            <button
              onClick={() => setToast(null)}
              className="text-white hover:text-gray-200 transition-colors ml-2"
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>
        </div>
      )}
      </div>
    </>
  );
};

export default SupplierAuctions;
